import type { ApiResponse } from '#shared/types/api'

// Response from API - matches database structure (snake_case)
interface AgeGroup {
  id: string
  name: string
  slug: string
  description: string | null
  sort_order: number | null
  is_active: boolean | null
  created_at: string | null
  updated_at: string | null
}

// Input payload for database - uses snake_case
interface CreateAgeGroupPayload {
  name: string
  description?: string
  sort_order?: number
  is_active?: boolean
}

export function useAgeGroups() {
  /**
   * Get all age groups
   */
  const getAgeGroups = async (activeOnly = false): Promise<ApiResponse<AgeGroup[]>> => {
    const query = activeOnly ? '?active_only=true' : ''

    return await $api(`/api/age-groups${query}`, {
      method: 'GET',
    })
  }

  /**
   * Get age group by ID
   */
  const getAgeGroupById = async (id: string): Promise<ApiResponse<AgeGroup>> => {
    return await $api(`/api/age-groups/${id}`, {
      method: 'GET',
    })
  }

  /**
   * Create a new age group
   */
  const createAgeGroup = async (payload: CreateAgeGroupPayload): Promise<ApiResponse<AgeGroup>> => {
    return await $api('/api/age-groups', {
      method: 'POST',
      body: payload,
    })
  }

  /**
   * Update an age group
   */
  const updateAgeGroup = async (
    id: string,
    payload: Partial<CreateAgeGroupPayload>,
  ): Promise<ApiResponse<AgeGroup>> => {
    return await $api(`/api/age-groups/${id}`, {
      method: 'PATCH',
      body: payload,
    })
  }

  /**
   * Delete an age group
   */
  const deleteAgeGroup = async (id: string): Promise<ApiResponse<void>> => {
    return await $api(`/api/age-groups/${id}`, {
      method: 'DELETE',
    })
  }

  return {
    getAgeGroups,
    getAgeGroupById,
    createAgeGroup,
    updateAgeGroup,
    deleteAgeGroup,
  }
}
